import React, {useRef, useCallback, useEffect, useMemo, useState} from 'react';
import {connect} from "react-redux";
import {compose} from "redux";
import CustomSnackbar from "../alert/CustomSnackbar";
import DataGrid from "react-data-grid";
import {
    CustomTableHeaderCellRender,
    GetSelectedRowID,
    IsAtBottom
} from "../reporting-table/ReactDataGridUtils";
import {debounce} from "lodash";
import Spinner from "../spinner/Spinner";
import CustomToolbar from "../utils/CustomToolbar";
import {CustomSelectRowColumn, DynamicGridColumnCreator, MarkColumn} from "../reporting-table/Columns";
import {campaignActions} from "../../store/actions/campaignsAction";
import {GetLocalStorageCache, SetLocalStorageCache} from "../cache/Cache";
import {groupBy1Action} from "../../store/actions/groupBy1Action";
import {groupBy2Action} from "../../store/actions/groupBy2Action";
import {groupBy3Action} from "../../store/actions/groupBy3Action";
import {columnActions} from "../../store/actions/columnAction";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {GetCommaSeparatedList} from "../../helpers/common";
import {Divider, useTheme} from "@mui/material";
import useCustomStyles from "../utils/UseCustomStyles";
import {CustomCampaignDataGridBox} from "./CampaignsView";
import {CampaignConfirmationDialog} from "./CampaignDesign";
import ReportEmptyState from '../reporting-table/ReportEmptyState';

const customStyles = (theme) => ({
    dataGrid: theme.defaultDataGrid
});

function CampaignsGrid(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {
        alert, getReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows, setSelectedRows,
        groupBy, columnGroup, report, getWorkspaceIDs, updateDetailsReportingView
    } = props;
    const {
        data, isLoading, errorMsg, name, tags, filterTrafficSources, orderBy, sortBy, sortColumns, rerender, selectedRows,
        detailsReportingView
    } = props.campaignsState;
    const {from, to, isDeleted} = props.ddToolbarState;
    const {timezone, currencyCode} = props.auth.userSettings;

    const workspaceIDs = (props?.workspace?.userWorkspaces || [])
        .filter(workspace => workspace?.selected && workspace?.workspaceId)
        .map(workspace => workspace.workspaceId).join(",");
    const workspacesReady = workspaceIDs !== "";

    const gridRef = useRef(null);
    const lastScrollTop = useRef(0);
    const [columns, setColumns] = useState(null);
    const columnsReady = columns !== null;

    const updateColumnResize = (idx, width) => {
        props.updateColumnWidth({columnKey: columns[idx]?.key, width, columnGroup, report});
    };

    const modifiedTableColumns = useMemo(() => {
        function CustomHeaderCell(props) {
            return <CustomTableHeaderCellRender {...props}/>;
        }

        return columns?.map((c) => {
            if (c?.key === "custom-select-row" || c?.key === "mark") {
                return c;
            }
            return {...c, renderHeaderCell: CustomHeaderCell};
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, columnGroup, report]);

    const getTableColumns = () => {
        let columns = [];
        props?.column?.data[columnGroup]?.forEach((col) => {
            if (!col?.hidden) {
                columns.push(col?.reportColumnKey);
            }
        });
        return columns;
    };

    const handleGetReportData = ({exportCSV = "", pageSize = 100}) => {
        if (!workspacesReady) {
            return;
        }
        lastScrollTop.current = 0;
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(groupBy, from, to, timezone, name, tags, filterTrafficSources, isDeleted, 1, pageSize, orderBy, sortBy, currencyCode, exportCSV, props?.metricFilter.filters, getTableColumns(), getWorkspaceIDs());
    };

    const handleInfinityScroll = (event) => {
        const {scrollTop} = event.currentTarget;
        const scrollingDown = scrollTop > lastScrollTop.current;
        lastScrollTop.current = scrollTop;

        if (!scrollingDown || isLoading || errorMsg || !workspacesReady || !IsAtBottom(event)) {
            return;
        }

        const hasMoreRows = data?.page > 0 && data?.recordsPerPage > 0 &&
            data.page < Math.ceil(data?.total?.totalRecords / data.recordsPerPage);
        if (hasMoreRows) {
            selectedRows.clear(); // Clear all selected values
            loadMoreRows(groupBy, from, to, timezone, name, tags, filterTrafficSources, isDeleted, data.page + 1, 100, orderBy, sortBy, currencyCode, props?.metricFilter.filters, getTableColumns(), getWorkspaceIDs());
        }
    };

    const handleRefresh = () => {
        if (!workspacesReady || isLoading) {
            return;
        }
        lastScrollTop.current = 0;
        gridRef?.current?.element?.scrollTo(0, 0);
        handleGetReportData({});
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(updateColumnResize, 500), [columns, columnGroup, report]);

    const handleGroupBySelectedOptions = (trafficSourceName) => {
        let cache = GetLocalStorageCache("groupBySelect");
        if (cache !== null && cache !== undefined && cache !== "") {
            for (const item of cache) {
                if (item?.name === trafficSourceName) {
                    props.updateGroupBy1(item.g1);
                    props.updateGroupBy2(item.g2);
                    props.updateGroupBy3(item.g3);
                    return false;
                }
            }
        } else {
            cache = [];
        }

        cache.push({
            name: trafficSourceName,
            g1: props.groupBy1State?.groupBy,
            g2: props.groupBy2State?.groupBy,
            g3: props.groupBy3State?.groupBy,
        });
        SetLocalStorageCache("groupBySelect", cache)
    };

    const handleColumnRender = () => {
        let columns = [
            CustomSelectRowColumn({setSelectedRows}),
            MarkColumn,
            ...DynamicGridColumnCreator({columns: props?.column?.data[columnGroup]})
        ];
        setColumns(columns);
    };

    // 1. Generate grid columns dynamically at once and reset integration data.
    useEffect(() => {
        if (props?.column?.data !== null && columns === null) {
            props?.resetIntegration();
            handleColumnRender();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.column?.data]);

    // 2. Get report data.
    useEffect(() => {
        if (columns !== null) {
            handleGetReportData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsReady, workspacesReady, workspaceIDs, from, to, timezone, name, tags, filterTrafficSources, isDeleted, sortBy, orderBy, currencyCode, props?.metricFilter?.filters]);

    // 3. Rerender
    useEffect(() => {
        if (rerender && columns !== null) {
            handleGetReportData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (columns !== null) {
            handleColumnRender();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.column?.data]);

    // 4. Update OrderBy and SortBy values.
    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            };
        }
        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);
    }, [updateOrderBy, updateSortBy, sortColumns]);

    // 5. Store Group By dropdown values to localstorage and set new values if campaign is selected.
    useEffect(() => {
        // Add filter by traffic source logic
        if (filterTrafficSources?.length === 1) {
            handleGroupBySelectedOptions(filterTrafficSources[0]?.label);
        } else {
            // At least 1 campaign must be selected if multiple traffic sources or no traffic source filter added.
            const rows = props?.campaignsState?.selectedRows;
            if (rows?.size === 1) {
                let trafficSourceName = "";
                props?.campaignsState?.data?.records?.forEach(item => {
                    if (item.id === GetSelectedRowID(rows)) {
                        trafficSourceName = item?.trafficSourceName;
                    }
                });
                handleGroupBySelectedOptions(trafficSourceName);
            }

            if (rows?.size > 1 || rows?.size === 0) {
                props.resetGroupBy1();
                props.resetGroupBy2();
                props.resetGroupBy3();
                return () => {
                };
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.campaignsState?.filterTrafficSources, props?.campaignsState?.selectedRows])

    // 6. Rerender if user workspaces has changed.
    useEffect(() => {
        if (props?.workspace?.updated !== null) {
            props?.resetUpdatedWorkspaceValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.workspace?.updated])

    return (
        <>
            <CustomToolbar
                confirmationDialog={CampaignConfirmationDialog}
                columns={columns}
                selectedRows={selectedRows}
                onChangeToPage={props.rerender}
                searchTerm={name}
                onChangeSearch={props.updateName}
                tagsTerm={tags}
                onChangeTags={props.updateTags}
                filterTrafficSourcesTerm={filterTrafficSources}
                onChangeFilterTrafficSources={props.updateFilterTrafficSources}
                onClickAdd={() => {
                    props.history.push({
                        pathname: "/campaigns/new",
                        state: {background: props.location}
                    });
                }}
                onClickRefresh={handleRefresh}
                onClickExportCSV={() => handleGetReportData({exportCSV: "csv", pageSize: 10000})}
                onClickEdit={(selectedRowId) => {
                    props.history.push({
                        pathname: "/campaigns/edit/" + selectedRowId,
                        state: {background: props.location}
                    });
                }}
                onClickDuplicate={(selectedRowId) => {
                    props.history.push({
                        pathname: "/campaigns/duplicate/" + selectedRowId,
                        state: {background: props.location}
                    });
                }}
                onClickArchive={(selectedRowsIds) => {
                    selectedRows.clear();
                    props.archive({ids: selectedRowsIds})
                }}
                onClickRestore={(selectedRowsIds) => {
                    selectedRows.clear();
                    props.restore({ids: selectedRowsIds})
                }}
                onClickCampaignLinks={(selectedRowId) => {
                    props.history.push({
                        pathname: "/campaigns/links/" + selectedRowId,
                        state: {background: props.location}
                    });
                }}
                onClickCampaignCost={(selectedRowId) => {
                    props.history.push({
                        pathname: "/campaigns/cost/" + selectedRowId,
                        state: {background: props.location}
                    });
                }}
                onSubmitChangeTrackingDomain={(data) => {
                    data.campaignIds = '' + GetCommaSeparatedList(selectedRows);
                    props?.changeTrackingDomain(data);
                }}
                onSubmitChangeWorkspace={(data) => {
                    data.campaignIds = '' + GetCommaSeparatedList(selectedRows);
                    props?.changeWorkspace(data);
                }}
                onlyCampaignView={true}
                report={report}
                columnGroup={columnGroup}
                detailsReportingView={detailsReportingView}
                onChangeDetailsReportingView={updateDetailsReportingView}
            />
            <Divider/>
            <CustomCampaignDataGridBox>

                {alert.errorMsg !== "" && <CustomSnackbar message={alert.errorMsg} timeout={3500}/>}
                {alert.successMsg !== "" &&
                    <CustomSnackbar message={alert.successMsg} severity="success" timeout={3500}/>}

                {(isLoading || columns === null) && <Spinner overComponentBox={true}/>}

                {columns !== null && classes !== null &&
                    <DataGrid
                        ref={gridRef}
                        rowKeyGetter={(row) => row?.id}
                        columns={modifiedTableColumns}
                        rows={data?.records ? data.records : []}
                        defaultColumnOptions={{
                            sortable: true,
                            resizable: true,
                            draggable: false
                        }}
                        onColumnResize={handleColumnResize}
                        sortColumns={sortColumns}
                        onSortColumnsChange={updateSortColumns}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={setSelectedRows}
                        onSelectedCellChange={({rowIdx, row, column}) => {
                            if (rowIdx !== -1 && column?.key !== "custom-select-row") {
                                if (props.groupBy1State.selectedRows.size > 0) {
                                    props.groupBy1State.selectedRows.clear();
                                }
                                if (props.groupBy2State.selectedRows.size > 0) {
                                    props.groupBy2State.selectedRows.clear();
                                }
                                setSelectedRows(new Set().add(row.id));
                            }
                        }}
                        onCellDoubleClick={props.handleDoubleClick}
                        onScroll={handleInfinityScroll}
                        summaryRowHeight={35}
                        bottomSummaryRows={[data]}
                        enableVirtualization={true}
                        className={classes?.dataGrid}
                    />
                }
                {workspacesReady && !isLoading && !errorMsg && !alert.errorMsg && columns !== null && classes !== null &&
                    data?.page > 0 && !data?.records?.length && <ReportEmptyState kind="campaigns" archived={isDeleted}/>}
            </CustomCampaignDataGridBox>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        campaignsState: state.campaign,
        groupBy1State: state.groupBy1,
        groupBy2State: state.groupBy2,
        groupBy3State: state.groupBy3,
        ddToolbarState: state.ddToolbar,
        auth: state.auth,
        alert: state.alert,
        metricFilter: state.metricFilter,
        column: state.column,
        workspace: state.workspace
    }
};

const actionCreators = {
    getReport: campaignActions.getReport,
    loadMoreRows: campaignActions.loadMoreRows,
    updateOrderBy: campaignActions.updateOrderBy,
    updateSortBy: campaignActions.updateSortBy,
    updateName: campaignActions.updateName,
    updateTags: campaignActions.updateTags,
    updateFilterTrafficSources: campaignActions.updateFilterTrafficSources,
    updateSortColumns: campaignActions.updateSortColumns,
    archive: campaignActions.archive,
    restore: campaignActions.restore,
    setSelectedRows: campaignActions.setSelectedRows,
    updateGroupBy1: groupBy1Action.updateGroupBy,
    resetIntegration: groupBy1Action.resetIntegration,
    resetGroupBy1: groupBy1Action.reset,
    updateGroupBy2: groupBy2Action.updateGroupBy,
    resetGroupBy2: groupBy2Action.reset,
    updateGroupBy3: groupBy3Action.updateGroupBy,
    resetGroupBy3: groupBy3Action.reset,
    updateColumnWidth: columnActions.updateColumnWidth,
    updateColumnSequence: columnActions.updateColumnSequence,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue,
    changeTrackingDomain: campaignActions.changeTrackingDomain,
    changeWorkspace: campaignActions.changeWorkspace,
    updateDetailsReportingView: campaignActions.updateDetailsReportingView,
};

export default compose(connect(mapStateToProps, actionCreators))(CampaignsGrid);
